'use client';

import { LucideIcon, BookOpen, FileText, TrendingUp, MonitorCheck } from 'lucide-react';
import { useBranding, BrandWordmark } from '@test-prep/b2b-branding-client';
import { useTranslations } from 'next-intl';
import { AuthBrandPanel, AuthMobileBar } from '@test-prep/ui';

interface AuthHeroFeature {
  icon: LucideIcon;
  label: string;
}

interface AuthHeroProps {
  title?: string;
  description?: string;
  features?: AuthHeroFeature[];
}

// Canonical left brand panel shared by every step of the auth funnel
// (login/register/forgot-password/verify-email via AuthLayout, and
// onboarding). Callers may override the heading/subtitle for their own
// step; the teal-700 patterned surface with the orange circle (the landing
// hero's look), boxed feature icons, and default feature list stay the single
// canonical look. Do not fork a variant.
export function AuthHero({ title, description, features }: AuthHeroProps) {
  const t = useTranslations('auth');
  const { appName } = useBranding();
  const landingUrl = process.env.NEXT_PUBLIC_LANDING_URL || '/';

  const resolvedFeatures: AuthHeroFeature[] = features ?? [
    { icon: BookOpen, label: t('featureQuestions') },
    { icon: FileText, label: t('featureExplanation') },
    { icon: TrendingUp, label: t('featureAnalytics') },
    { icon: MonitorCheck, label: t('featureSimulation') },
  ];

  return (
    <AuthBrandPanel
      brand={<BrandWordmark accentClassName="text-teal-200" />}
      homeHref={landingUrl}
      title={title ?? t('brandingTitle')}
      description={description ?? t('brandingDesc')}
      features={resolvedFeatures}
      footer={<span suppressHydrationWarning>&copy; {new Date().getFullYear()} {appName}</span>}
    />
  );
}

interface AuthHeroMobileBarProps {
  showBackLink?: boolean;
}

// Compact brand bar shown in place of AuthHero on viewports below `lg`,
// where the panel above is hidden entirely. Kept as a sibling export
// (rather than folded into AuthHero's own markup) because it lives
// nested inside each page's own right-hand column — not alongside the
// desktop panel — so it can't share a single root with it.
export function AuthHeroMobileBar({ showBackLink = false }: AuthHeroMobileBarProps) {
  const t = useTranslations('auth');
  const landingUrl = process.env.NEXT_PUBLIC_LANDING_URL || '/';

  return (
    <AuthMobileBar
      brand={<BrandWordmark accentClassName="text-teal-600" />}
      homeHref={landingUrl}
      backLabel={showBackLink ? t('backToHome') : undefined}
    />
  );
}
